import React from "react";
import { HighContrastTheme } from "../constants";
import { getHighContrastTheme } from "../browserHelper";
import { ExtensionType, getImageSource } from "../imageHelpers/imagesHelper";

/**
 * If file extension is not supplied, SVG will be used if supported,
 *  else PNG will be used.
 */
export type AccessibleImageType = { dark: string; light: string; extension?: ExtensionType };

interface IAccessibleImageProps {
  accessibleImages: AccessibleImageType;
  style?: string;
  isUrl?: boolean;
  altText?: string;
  role?: string;
}

/**
 * Returns light image for high contrast dark or if hasDefaultDarkBackground is true and
 * high contrast light theme is not enabled.
 * @param name The image name(s) for different background types
 * @returns A path to the appropriate accessible image or empty string
 */
export function useAccessibleImage(name?: AccessibleImageType): string {
  if (!name) {
    return "";
  }

  const hcTheme = getHighContrastTheme();
  const hasDefaultDarkBackground  = false;

  return hcTheme === HighContrastTheme.dark ||
    (hasDefaultDarkBackground && hcTheme !== HighContrastTheme.light)
    ? name.light
    : name.dark;
}

/**
 * AccessibleImage component
 * @param props IAccessibleImageProps
 * @returns img with the appropriate source (dark or light) based on high contrast settings.
 */
export const AccessibleImage: React.FC<IAccessibleImageProps> = (props) => {
  const { accessibleImages, style, isUrl = false, altText = "", role = "img" } = props;
  const image = useAccessibleImage(accessibleImages);

  if (!image) {
    return null;
  }

  //tslint:disable : use-simple-attributes
  return (
    <img
      data-testid="accessibleImg"
      className={style}
      src={isUrl ? image : getImageSource(image, accessibleImages.extension)}
      alt={altText}
      role={role}
    />
  );
};
